import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
} from '@mui/material'
import React from 'react'
import ResponsiveAppBar from 'src/common/ResponsiveAppBar'
import { inject } from 'mobx-react'
import { IStores } from 'src/Stores'
import _ from 'lodash'
import { InboxActionSummary } from 'src/viewModels/workflow/InboxActionSummary'
import { processAction, sendToOcrValidation } from 'src/services/WorkflowService'
import { ObjectiveProcessAction } from 'src/viewModels/workflow/ObjectiveProcessAction'
import { OptionsObject } from 'notistack'

interface ISendToOcrModalProps {
  isOpen: boolean
  closeModal: () => void
  action: InboxActionSummary
  rerouteToPath?: (path: string) => void
  inboxItemId: number
  inboxId: string
  associatedItemId: string
  data: ObjectiveProcessAction
  actionList: InboxActionSummary[]
  sendNotification?: (
    key: string,
    message: string,
    options?: OptionsObject,
    information?: string | undefined
  ) => void
  handleProcessActionFinish?: (selectedAction?: InboxActionSummary) => void
}

class SendToOcrModal extends React.Component<ISendToOcrModalProps> {
  public state = {
    errors: new Array<string>(),
    warnings: new Array<string>(),
    executed: false,
    isLoading: true,
    comments: '',
  }

  public componentDidMount(): void {
    this.setState({ isLoading: true })
    if (this.props.data.claimData) {
      sendToOcrValidation(this.props.associatedItemId, this.props.data.claimData).then(
        (result) => {
          this.setState({
            errors: result.errors,
            isLoading: false,
            warnings: result.warnings,
          })
        }
      )
    } else {
      this.props.sendNotification!(
        '404',
        'Claim data is missing, please refresh the page.',
        {
          variant: 'error',
        }
      )
      this.close()
    }
  }

  private async save() {
    var action = this.props.action
    this.props.data.comment = this.state.comments

    if (this.state.errors.length > 0) {
      this.setState({ executed: false })
      return
    }

    processAction(
      this.props.data,
      action!.id.toString(),
      this.props.inboxItemId.toString()
    )
      .then(() => {
        this.props.handleProcessActionFinish!(action)
        this.close()
      })
      .catch(() => {
        this.setState({ executed: false, isLoading: false })
        this.close()
      })
  }

  private close = () => {
    this.setState({
      errors: new Array<string>(),
      enableContractTBD: false,
      executed: false,
      tbdActionId: '',
      isLoading: false,
    })
    this.props.closeModal()
  }

  public render() {
    var modalTitle = this.props.action.actionName.toUpperCase()

    return (
      <>
        <Dialog
          key={this.props.action.id}
          fullWidth={true}
          maxWidth="md"
          open={this.props.isOpen!}
          onClose={(_, reason) => {
            if (reason !== 'backdropClick') {
              this.close()
            }
          }}
        >
          <DialogTitle style={{ padding: 0 }}>
            <ResponsiveAppBar title={modalTitle} isModal={true} />
          </DialogTitle>
          <DialogContent style={{ padding: '12px 12px 8px' }}>
            {this.state.isLoading && (
              <div
                style={{
                  position: 'fixed',
                  display: 'flex',
                  width: '100%',
                  height: '100%',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  cursor: 'pointer',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: '1200',
                }}
              >
                <CircularProgress size={100} />
              </div>
            )}
            <Grid container direction="column" spacing={2}>
              <Grid item xs={12}>
                {this.state.errors.length > 0 || this.state.warnings?.length > 0 ? (
                  <>
                    {this.state.errors.length > 0 && (
                      <>
                        <h2 style={{ color: 'red' }}>Validation Errors</h2>
                        <ul style={{ color: 'red' }}>
                          {this.state.errors.map((error) => (
                            <li>{error}</li>
                          ))}
                        </ul>
                      </>
                    )}
                  </>
                ) : this.state.isLoading ? (
                  <></>
                ) : (
                  <h2 style={{ color: 'green' }}>
                    Success! Claim is ready to go to OCR.
                  </h2>
                )}
              </Grid>
              {this.props.action.displayComment && (
                <Grid item xs={12}>
                  <TextField
                    label="Comments"
                    name="notes"
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    required={this.props.action.requireComment}
                    multiline
                    minRows="4"
                    disabled={this.state.errors.length > 0}
                    onChange={(event) => this.setState({ comments: event.target.value })}
                  />
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button
              variant="contained"
              color="primary"
              disabled={
                this.state.executed ||
                this.state.errors.length > 0 ||
                (this.state.comments === '' && this.props.action.requireComment)
              }
              onClick={() => {
                this.setState({ executed: true })
                this.save()
              }}
            >
              Execute
            </Button>
            <Button
              onClick={() => {
                this.close()
              }}
              data-cy="close"
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </>
    )
  }
}

const InjectedSendToOcrModal = inject<
  IStores,
  ISendToOcrModalProps,
  Partial<ISendToOcrModalProps>,
  any
>((stores: IStores) => ({
  rerouteToPath: stores.global.rerouteToPath,
  sendNotification: stores.notifications.sendNotification,
  selectedLocation: stores.locations.selectedEditClaimLocation,
}))(SendToOcrModal)

export default InjectedSendToOcrModal
